import React, { useMemo } from 'react';
import { Box, Skeleton, Stack, Typography } from '@mui/material';
import { BarChart } from '@mui/x-charts';
import { LenderRetailActivityLenderRowViewModel } from '../../../api/types';
import { formatCompactAmount, getCountryLabel, getFinancedBarColor, groupLenderRowsByCountry } from '../../../lib';
import { getCountryFlag } from './country';

interface Props {
  rows: LenderRetailActivityLenderRowViewModel[];
  loading?: boolean;
}

const CHART_HEIGHT = 280;
// Rough average glyph width (px) for the bar label font, used to decide if a label fits inside its bar
const BAR_LABEL_CHAR_WIDTH = 7;
const BAR_LABEL_PADDING = 8;

const getBarLabelForWidth = (value: number | null, barWidth: number): string => {
  if (value == null) return '';

  const text = formatCompactAmount(value);
  const estimatedTextWidth = text.length * BAR_LABEL_CHAR_WIDTH + BAR_LABEL_PADDING;

  // Hide the label when the bar is too small to fit it, so it doesn't overflow into the axis and get clipped
  return estimatedTextWidth <= barWidth ? text : '';
};

export const RequestedFinancing = ({ rows, loading }: Props) => {
  const groups = useMemo(
    () =>
      groupLenderRowsByCountry(rows)
        .map(group => ({
          ...group,
          rows: group.rows.filter(row => row.requestedAmount > 0).sort((a, b) => b.requestedAmount - a.requestedAmount),
        }))
        .filter(group => group.rows.length > 0),
    [rows]
  );

  return (
    <Stack gap={1}>
      <Typography variant="subtitle2" component="span">
        Requested financing ($)
      </Typography>

      <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
        {loading ? (
          <Skeleton variant="rounded" height={CHART_HEIGHT} />
        ) : !groups.length ? (
          <Typography sx={{ mt: 2 }} color="text.secondary">
            No data
          </Typography>
        ) : (
          <Stack gap={3}>
            {groups.map(group => {
              const flag = getCountryFlag(group.country);

              return (
                <Box key={group.country}>
                  <Stack direction="row" alignItems="center" gap={0.5} sx={{ mb: 0.5 }}>
                    {flag && <Box component={flag} sx={{ width: 16, height: 16 }} />}
                    <Typography variant="subtitle2" component="span">
                      {getCountryLabel(group.country)}
                    </Typography>
                  </Stack>

                  <BarChart
                    layout="horizontal"
                    height={Math.max(group.rows.length * 40 + 40, 120)}
                    margin={{ left: 160, top: 10, right: 40, bottom: 24 }}
                    yAxis={[{ scaleType: 'band', data: group.rows.map(row => row.lenderName) }]}
                    xAxis={[{ min: 0, valueFormatter: value => formatCompactAmount(Number(value)) }]}
                    series={[
                      {
                        data: group.rows.map(row => row.requestedAmount),
                        color: getFinancedBarColor(group.country),
                        valueFormatter: value => (value == null ? '' : formatCompactAmount(value)),
                      },
                    ]}
                    barLabel={(item, context) => getBarLabelForWidth(item.value, context.bar.width)}
                    slotProps={{ legend: { hidden: true } }}
                  />
                </Box>
              );
            })}
          </Stack>
        )}
      </Box>
    </Stack>
  );
};
